import { useTranslation } from 'react-i18next'
import { FormControlLabelProps, RadioProps, TypographyProps } from '@mui/material'
import Box from '@mui/material/Box'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import FormGroup from '@mui/material/FormGroup'
import FormLabel from '@mui/material/FormLabel'
import Typography from '@mui/material/Typography'
import { useFormikContext } from 'formik'
import { fontFamilies } from 'theme/styles.utils'
import { TranslationsKeys } from 'utils/createTranslationKey'

export interface ControlLabel<T = unknown> extends Partial<FormControlLabelProps> {
  label: TranslationsKeys
  description?: TranslationsKeys
  value: T
}
export interface FormikCheckboxGroupProps<T = unknown> {
  name: string
  controlsProps?: RadioProps
  controlLabels: ControlLabel<T>[]
  labelProps?: TypographyProps<'span', {}> | undefined
  disabled?: boolean
  errorProps?: TypographyProps<'span', {}> | undefined
  label: TranslationsKeys
  onChange?: (value: T, checked: boolean) => void
  value?: T[]
}

export const FormikCheckboxGroup = <T = unknown,>({
  name,
  controlsProps = { color: 'primary' },
  controlLabels = [],
  labelProps,
  label,
  disabled,
  errorProps,
  onChange,
  value,
  ...props
}: FormikCheckboxGroupProps<T>) => {
  const { t } = useTranslation()
  const { getFieldMeta, setFieldValue } = useFormikContext()
  const { error, touched, value: formikValue } = getFieldMeta<unknown[]>(name)

  const selectedValues = value || formikValue

  return (
    <Box sx={{ display: 'grid', ml: -0.5 }}>
      <FormControl sx={{ m: 0.5 }} component="fieldset" variant="standard">
        <FormLabel
          component="legend"
          sx={{ fontFamily: fontFamilies.cond, color: (theme) => `${theme.palette.text.primary}!important` }}
        >
          {t(label)}
        </FormLabel>
        <FormGroup>
          {controlLabels?.map(({ value, label, description, ...elProps }, i) => (
            <FormControlLabel
              control={
                <Checkbox
                  checked={selectedValues?.includes(value)}
                  onChange={(event, checked) => {
                    if (onChange) {
                      onChange(value, checked)
                    } else {
                      if (checked) {
                        setFieldValue(name, [...formikValue, value])
                      } else {
                        setFieldValue(name, formikValue?.filter((v) => v !== value) || [])
                      }
                    }
                  }}
                  {...controlsProps}
                />
              }
              label={
                <Typography variant="body1" fontWeight="bold">
                  {t(label)}
                  {description && (
                    <Typography component="span" variant="h5" fontWeight="400" sx={{ ml: 1 }}>
                      {t(description)}
                    </Typography>
                  )}
                </Typography>
              }
              value={value}
              key={i}
              componentsProps={{
                typography: labelProps,
              }}
              disabled={disabled}
              {...elProps}
            />
          ))}
        </FormGroup>
      </FormControl>
      {touched && error && (
        <Typography color="error" {...errorProps}>
          {error}
        </Typography>
      )}
    </Box>
  )
}
